import { useState } from "react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const pyramidTiers = [
  { label: "Solo practitioners", count: "~95,000", color: "#E5E5E0", width: "100%", dark: false },
  { label: "Dev shops / agencies", count: "~2,200", color: "#C4C4BC", width: "75%", dark: false },
  { label: "Marketplaces", count: "~4,000 profiles", color: "#7B9BC0", width: "55%", dark: true },
  { label: "Boutique consulting", count: "<50 firms", color: "#002178", width: "40%", dark: true },
  { label: "Big consulting", count: "~15 firms", color: "#2A2A2A", width: "28%", dark: true },
];

const tableRows = [
  {
    type: "Solo practitioner",
    problemType: "Fill a seat",
    model: "Hourly or monthly retainer",
    structure: "Individual",
    engagement: "3–6 months",
    growth: "$3K–10K/mo",
    midmarket: "$5K–15K/mo",
    enterprise: "$10K–25K/mo",
    highlight: false,
  },
  {
    type: "Dev shop / agency",
    problemType: "Fill a seat",
    model: "Bundled into dev retainer",
    structure: "Team, product is bolt-on",
    engagement: "Project-based",
    growth: "$4K–12K/mo",
    midmarket: "$8K–20K/mo",
    enterprise: "—",
    highlight: false,
  },
  {
    type: "Marketplace",
    problemType: "Fill a seat / Fix a function",
    model: "Monthly retainer, hourly",
    structure: "Individual, platform-matched",
    engagement: "3–12 months",
    growth: "$5K–15K/mo",
    midmarket: "$8K–25K/mo",
    enterprise: "$15K–40K/mo",
    highlight: false,
  },
  {
    type: "Boutique consulting",
    problemType: "Deliver the growth you promised",
    model: "Monthly retainer, scoped",
    structure: "Embedded team, 2–12 people",
    engagement: "6–18 months",
    growth: "—",
    midmarket: "From $25K/mo",
    enterprise: "From $50K/mo",
    highlight: false,
  },
  {
    type: "Big consulting",
    problemType: "Fix a function, strategy only",
    model: "Fixed fee, T&M w/ cap",
    structure: "Team, 3–8 people",
    engagement: "2–6 months",
    growth: "—",
    midmarket: "—",
    enterprise: "$400K–2M+ total",
    highlight: false,
  },
];

const mistakes = [
  {
    title: "Hiring a solo when you need a team",
    body: "One person in a $100M+ company adapts to your culture, your pace, your dysfunction. They won't push back. They won't change the trajectory. 62% of companies that start with a solo practitioner upgrade to a firm within 12 months.",
  },
  {
    title: "Getting a deck instead of execution",
    body: "Big consulting delivers frameworks. One CIO told us they spent six months and $2M waiting for a governance framework that never shipped a single feature. The deliverable was a PowerPoint. The product didn't move.",
  },
  {
    title: "Buying staff aug and calling it leadership",
    body: "Marketplace placements and dev shop add-ons are staff augmentation with better titles. They execute your plan. If your plan is the problem, they'll execute it faster. In the wrong direction.",
  },
  {
    title: "Waiting for the perfect hire",
    body: "A full-time CPO search takes 4–6 months. Onboarding takes another 3. A wrong hire costs 18 months to recover from. Every month of delay burns runway and compounds the problem.",
  },
];

const faqs = [
  {
    question: "How much does a fractional CPO cost per month?",
    answer:
      "It depends on what you're buying. A solo practitioner runs $5K–15K/month. A marketplace placement runs $8K–25K after markup. A boutique consulting engagement starts at $25K–50K/month depending on company size. Big consulting product strategy projects start at $400K. The right question isn't how much. It's whether you need someone to execute your plan or a team that owns the outcome.",
  },
  {
    question: "What's the difference between a fractional CPO and a consultant?",
    answer:
      "A consultant diagnoses problems and delivers recommendations. An embedded product leadership team joins your org, attends your standups, makes decisions, ships product, and is accountable for outcomes. One gives you a deck. The other gives you results.",
  },
  {
    question: "When should I hire a fractional CPO instead of a full-time CPO?",
    answer:
      "When you need speed. A full-time CPO search takes 4–6 months. Onboarding takes another 3. A wrong hire costs 18 months to recover from. An embedded team can be deployed in weeks and delivering impact in the first quarter.",
  },
  {
    question: "How long do fractional CPO engagements last?",
    answer:
      "Typical engagements run 6–18 months, with quarterly check-ins on scope and outcomes. Some are shorter. A 90-day phase one that answers 'should we even build this?' can be as valuable as a year-long engagement.",
  },
  {
    question: "Can a fractional CPO work with my existing product team?",
    answer:
      "That's the whole point. We embed alongside your team, not above them. The model is player-coaches: we do the work alongside your people, build acknowledged competence, and the best internal talent starts emulating the approach. When we leave, the capability stays.",
  },
];

const FractionalCPOCost = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="How much does a fractional CPO cost?"
        description="We analyzed 100,000+ providers across 5 categories. Pricing by company size, engagement models, and what each actually delivers."
        path="/fractional-cpo-cost"
        image="/og-fractional-cpo-cost.png"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "How much does a fractional CPO cost?",
          description:
            "Market guide analyzing 100,000+ fractional product leadership providers across 5 categories.",
          dateModified: "2026-06-16",
        }}
      />

      {/* Section 1: Hero */}
      <section
        className="py-20 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-white/50 mb-8 font-medium"
          >
            Market guide
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-8 text-white"
          >
            How much does a fractional CPO{" "}
            <span className="font-medium">cost?</span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl"
          >
            <p className="text-lg text-white/60 leading-relaxed">
              We looked at 100,000+ providers across 5 categories to find out.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 2: Summary + Pyramid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <h2 className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8">
                The short answer
              </h2>
              <div className="space-y-5">
                <p className="text-lg text-black/60 leading-relaxed">
                  Product leadership help costs between $5,000/month for a
                  solo practitioner and $2M+ for a big consulting engagement.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  The right option depends on three things: the problem
                  you're solving, your company size, and how fast you need
                  results.
                </p>
                <p className="text-lg text-black/60 leading-relaxed">
                  The market has over 100,000 providers. We organized them
                  into 5 categories.
                </p>
              </div>
            </motion.div>

            <div className="flex flex-col gap-4">
              {pyramidTiers.map((tier, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  style={{
                    width: tier.width,
                    backgroundColor: tier.color,
                    marginLeft: "auto",
                    marginRight: "auto",
                  }}
                  className="py-3 px-5 rounded-sm flex justify-between items-center w-full min-h-[52px]"
                >
                  <span
                    className={`text-sm font-medium ${tier.dark ? "text-white" : "text-black/70"}`}
                  >
                    {tier.label}
                  </span>
                  <span
                    className={`text-xs ml-6 whitespace-nowrap ${tier.dark ? "text-white/60" : "text-black/40"}`}
                  >
                    {tier.count}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Data table */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            What does each{" "}
            <span style={{ color: NAVY }} className="font-medium">
              cost?
            </span>
          </motion.h2>

          {/* Desktop table */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="hidden lg:block overflow-x-auto"
          >
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-black/10">
                  <th scope="col" className="text-left py-4 pr-4 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    <span className="sr-only">Provider type</span>
                  </th>
                  <th className="text-left py-4 px-3 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    Problem
                  </th>
                  <th className="text-left py-4 px-3 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    Pricing model
                  </th>
                  <th className="text-left py-4 px-3 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    What you get
                  </th>
                  <th className="text-center py-4 px-3 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    Growth
                    <span className="block text-xs normal-case tracking-normal text-black/30">&lt;$50M</span>
                  </th>
                  <th className="text-center py-4 px-3 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    Mid-market
                    <span className="block text-xs normal-case tracking-normal text-black/30">$50M–500M</span>
                  </th>
                  <th className="text-center py-4 px-3 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    Enterprise
                    <span className="block text-xs normal-case tracking-normal text-black/30">$500M+</span>
                  </th>
                  <th className="text-left py-4 px-3 text-sm uppercase tracking-[0.15em] text-black/40 font-medium">
                    Engagement
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-black/5 hover:bg-black/[0.02] transition-colors"
                  >
                    <td className="py-5 pr-4 text-sm text-black/70">
                      {row.type}
                    </td>
                    <td className="py-5 px-3 text-sm text-black/60">
                      {row.problemType}
                    </td>
                    <td className="py-5 px-3 text-sm text-black/60">
                      {row.model}
                    </td>
                    <td className="py-5 px-3 text-sm text-black/60">
                      {row.structure}
                    </td>
                    <td className="py-5 px-3 text-sm text-center text-black/60">
                      {row.growth}
                    </td>
                    <td className="py-5 px-3 text-sm text-center text-black/60">
                      {row.midmarket}
                    </td>
                    <td className="py-5 px-3 text-sm text-center text-black/60">
                      {row.enterprise}
                    </td>
                    <td className="py-5 px-3 text-sm text-black/60">
                      {row.engagement}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>

          {/* Mobile/tablet cards */}
          <div className="lg:hidden space-y-6">
            {tableRows.map((row, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ delay: i * 0.08 }}
                className="border-b border-black/10 pb-6"
              >
                <p className="text-base font-medium text-black/70 mb-3">
                  {row.type}
                </p>
                <div className="space-y-2 text-sm text-black/60">
                  <p>
                    <span className="font-medium text-black/40 uppercase tracking-[0.1em] text-xs">
                      Problem
                    </span>{" "}
                    {row.problemType}
                  </p>
                  <p>
                    <span className="font-medium text-black/40 uppercase tracking-[0.1em] text-xs">
                      Pricing model
                    </span>{" "}
                    {row.model}
                  </p>
                  <p>
                    <span className="font-medium text-black/40 uppercase tracking-[0.1em] text-xs">
                      What you get
                    </span>{" "}
                    {row.structure}
                  </p>
                  <p>
                    <span className="font-medium text-black/40 uppercase tracking-[0.1em] text-xs">
                      Engagement
                    </span>{" "}
                    {row.engagement}
                  </p>
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-black/5">
                    <div>
                      <p className="text-xs text-black/30 mb-1">Growth</p>
                      <p className="text-sm">{row.growth}</p>
                    </div>
                    <div>
                      <p className="text-xs text-black/30 mb-1">Mid-market</p>
                      <p className="text-sm">{row.midmarket}</p>
                    </div>
                    <div>
                      <p className="text-xs text-black/30 mb-1">Enterprise</p>
                      <p className="text-sm">{row.enterprise}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: How to find the best for you */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            How to find the best for{" "}
            <span style={{ color: NAVY }} className="font-medium">
              you?
            </span>
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0 }}
              className="border border-black/10 p-8 shadow-sm"
            >
              <p className="text-xl font-medium text-[#1a1a1a] mb-4">
                Deliver the growth you promised
              </p>
              <p className="text-base text-black/60 leading-relaxed mb-5">
                Revenue targets aren't being hit. Product is a feature factory. Nobody can answer "where will we play?"
              </p>
              <p className="text-sm font-medium text-black/40 uppercase tracking-[0.1em] mb-3">
                You're here if
              </p>
              <ul className="space-y-2 text-sm text-black/60">
                <li>• CEO is asking why competitors ship faster</li>
                <li>• You've tried solos or marketplace hires without results</li>
                <li>• The problem is systemic, not a single gap</li>
              </ul>
              <p className="text-sm font-medium text-[#002178] mt-5">
                → Embedded team with partner oversight
              </p>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="border border-black/10 p-8 shadow-sm"
            >
              <p className="text-xl font-medium text-[#1a1a1a] mb-4">
                Fix a function
              </p>
              <p className="text-base text-black/60 leading-relaxed mb-5">
                Leadership gap. No clear strategic direction. Team underwater and can't articulate what help they need.
              </p>
              <p className="text-sm font-medium text-black/40 uppercase tracking-[0.1em] mb-3">
                You're here if
              </p>
              <ul className="space-y-2 text-sm text-black/60">
                <li>• Your product leader isn't thinking strategically</li>
                <li>• Process is broken or doesn't exist</li>
                <li>• You know roughly what's wrong but need someone who's fixed it before</li>
              </ul>
              <p className="text-sm font-medium text-[#002178] mt-5">
                → Experienced product leader with methodology
              </p>
            </motion.div>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="border border-black/10 p-8 shadow-sm"
            >
              <p className="text-xl font-medium text-[#1a1a1a] mb-4">
                Fill a seat
              </p>
              <p className="text-base text-black/60 leading-relaxed mb-5">
                Clear scope. Known deliverable. Need someone to manage ICs and execute the roadmap.
              </p>
              <p className="text-sm font-medium text-black/40 uppercase tracking-[0.1em] mb-3">
                You're here if
              </p>
              <ul className="space-y-2 text-sm text-black/60">
                <li>• The role is well-defined</li>
                <li>• You have strategic direction, just need execution capacity</li>
                <li>• Budget matters more than speed to impact</li>
              </ul>
              <p className="text-sm font-medium text-[#002178] mt-5">
                → Solo practitioner or marketplace
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 5: Testimonial #1 */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
            <p className="text-lg md:text-xl font-light leading-relaxed text-[#1a1a1a] italic">
              "We tried 3 freelancers from a marketplace, and 2 dev shops over
              the past two years. None of them could move the needle at our
              scale. When we brought in FractionalCPO, the difference was
              immediate. They understood problems our internal team couldn't
              even articulate. They set a new standard for execution precision
              and speed in our company."
            </p>
            <p className="text-sm text-black/40 mt-4 font-medium uppercase tracking-[0.1em]">
              COO, PE-backed SaaS company, $800M revenue
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 6: What to avoid */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            {...fadeUp}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-12"
          >
            What to{" "}
            <span style={{ color: NAVY }} className="font-medium">
              avoid?
            </span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12">
            {mistakes.map((m, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <p className="text-lg font-medium text-[#1a1a1a] mb-3">
                  {m.title}
                </p>
                <p className="text-base text-black/60 leading-relaxed">
                  {m.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Where does fCPO fit? */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-3xl">
            <motion.h2
              {...fadeUp}
              className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
            >
              Where does FractionalCPO{" "}
              <span className="font-medium">fit?</span>
            </motion.h2>

            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="space-y-5 mb-10"
            >
              <p className="text-lg text-white/70 leading-relaxed">
                We're a boutique product leadership firm, 10+ years running.
                We deploy embedded teams of seasoned operators under partner
                supervision. Not individuals. Not staff aug. Not a deck that
                sits in a drawer.
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                Product leadership only. No engineering, no design. We use
                your team or our preferred partners. Our AI-native operating
                model means we make more decisions, faster, with higher
                confidence. Speed to impact, not a 6-month ramp.
              </p>
              <p className="text-lg text-white/70 leading-relaxed">
                We work with $100M–$10B annual revenue companies. Turnaround,
                acceleration, hitting growth targets the internal team can't
                match, 0-to-1 product, reorgs, leadership vacancies. We've
                reversed decade-long declines and doubled conversion rates.
                Not by building new features, but by finding the 5% of product
                work that actually moves the needle.
              </p>
            </motion.div>

            <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }}>
              <button
                onClick={() => {
                  trackEvent("contact_click", { source: "cta_block" });
                  setIsContactOpen(true);
                }}
                className="inline-block px-8 py-4 bg-white text-[#002178] font-medium hover:scale-105 transition-transform"
              >
                Talk to us
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 8: Testimonial #2 */}
      <section className="py-12 md:py-16 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
            <p className="text-lg md:text-xl font-light leading-relaxed text-[#1a1a1a] italic">
              "In the first quarter, they doubled our activation rate without
              building a single new feature. They found the 5% of product work
              that actually moved the needle. And then they shipped it."
            </p>
            <p className="text-sm text-black/40 mt-4 font-medium uppercase tracking-[0.1em]">
              CEO, PE-backed SaaS company, 1,200 employees
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 9: FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently asked questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default FractionalCPOCost;
